import Phaser from 'phaser';

/**
 * **As caixas que ficam onde uma nave caiu.**
 *
 * ## O que ela comunica, e o que não
 *
 * Um baú técnico escuro, com fechos em ciano e um detalhe dourado discreto: a
 * leitura tem de ser "há qualquer coisa ali para levar". Nunca uma explosão,
 * nunca dano, e **nunca uma raridade** — o que está lá dentro é privado, e uma
 * cor que o sugerisse contaria a toda a gente o que só quem abre pode saber.
 *
 * Uma caixa ao alcance ganha um realce a mais. É a única diferença visual entre
 * caixas, e ela não é sobre o conteúdo: é sobre a nave de quem olha.
 *
 * ## O que ela nunca decide
 *
 * Nada. A posição, a existência e o tempo que resta vêm do estado autoritativo,
 * e o desenho segue-os. Uma caixa esvaziada ou expirada desaparece porque o
 * servidor deixou de a publicar, e não porque a cena decidiu.
 *
 * ## Recursos
 *
 * Três texturas procedurais criadas **uma vez**, e três imagens por caixa,
 * indexadas pelo id público dela. Nada é criado por quadro: uma caixa que sai da
 * vista é destruída, e o `destroy` larga tudo. Sem `Math.random` — a variação é
 * uma dobra sobre o id, portanto duas máquinas desenham a mesma caixa da mesma
 * maneira.
 *
 * ## Pendente de avaliação
 *
 * A aparência **não está aprovada**, e nenhuma alegação de desempenho, de
 * ausência de engasgo ou de ausência de fuga é feita: nada foi medido num
 * navegador.
 */

const TEXTURE = {
  body: 'open-world-box-body',
  glow: 'open-world-box-glow',
  halo: 'open-world-box-halo',
} as const;

const BODY_PX = 64;
const GLOW_PX = 96;
const HALO_PX = 128;

const BODY_TINT = 0x1b2a3c;
const CLASP_TINT = 0x5fd0ff;
const REWARD_TINT = 0xffd24a;
const HALO_TINT = 0x5fd0ff;

/** Quanto tempo antes de expirar a caixa começa a piscar, em milissegundos. */
const FADING_MS = 30_000;

/** O que a cena precisa de saber. Onde, e quanto tempo resta. */
export interface BoxDrawState {
  readonly id: string;
  readonly x: number;
  readonly y: number;
  readonly ttl: number;
}

interface BoxView {
  readonly body: Phaser.GameObjects.Image;
  readonly glow: Phaser.GameObjects.Image;
  readonly halo: Phaser.GameObjects.Image;
}

interface BoxGraphics {
  fillStyle(color: number, alpha?: number): BoxGraphics;
  fillRect(x: number, y: number, width: number, height: number): BoxGraphics;
  fillCircle(x: number, y: number, radius: number): BoxGraphics;
  lineStyle(width: number, color: number, alpha?: number): BoxGraphics;
  strokeCircle(x: number, y: number, radius: number): BoxGraphics;
  strokeRect(x: number, y: number, width: number, height: number): BoxGraphics;
  lineBetween(x1: number, y1: number, x2: number, y2: number): BoxGraphics;
  generateTexture(key: string, width?: number, height?: number): unknown;
  destroy(): void;
}

interface TextureScene {
  readonly textures: { exists(key: string): boolean };
  readonly make: {
    graphics(config: { x: number; y: number }, addToScene: boolean): BoxGraphics;
  };
}

/**
 * **O corpo**: um baú com tampa, fechos e um friso.
 *
 * Branco, para ser tingido. A silhueta é compacta e assimétrica no eixo
 * vertical — a tampa é mais estreita do que a base — porque é isso que a faz
 * ler-se como um baú e não como um quadrado.
 */
function makeBodyTexture(scene: TextureScene): void {
  if (scene.textures.exists(TEXTURE.body)) {
    return;
  }
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  const margin = BODY_PX * 0.14;
  const width = BODY_PX - margin * 2;
  graphics.fillStyle(0xffffff, 1);
  graphics.fillRect(margin, BODY_PX * 0.42, width, BODY_PX * 0.42);
  graphics.fillRect(margin + width * 0.06, BODY_PX * 0.24, width * 0.88, BODY_PX * 0.2);
  graphics.lineStyle(2, 0xffffff, 0.55);
  graphics.strokeRect(margin, BODY_PX * 0.42, width, BODY_PX * 0.42);
  /* O friso onde a tampa encontra a base, e dois fechos. */
  graphics.lineStyle(3, 0xffffff, 0.9);
  graphics.lineBetween(margin, BODY_PX * 0.44, margin + width, BODY_PX * 0.44);
  graphics.fillRect(margin + width * 0.2, BODY_PX * 0.38, width * 0.1, BODY_PX * 0.12);
  graphics.fillRect(margin + width * 0.7, BODY_PX * 0.38, width * 0.1, BODY_PX * 0.12);
  graphics.generateTexture(TEXTURE.body, BODY_PX, BODY_PX);
  graphics.destroy();
}

/** Os fechos e o friso, numa camada própria para poderem ter outra cor. */
function makeGlowTexture(scene: TextureScene): void {
  if (scene.textures.exists(TEXTURE.glow)) {
    return;
  }
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  const centre = GLOW_PX / 2;
  graphics.fillStyle(0xffffff, 1);
  graphics.fillRect(centre - GLOW_PX * 0.05, centre - GLOW_PX * 0.05, GLOW_PX * 0.1, GLOW_PX * 0.1);
  graphics.generateTexture(TEXTURE.glow, GLOW_PX, GLOW_PX);
  graphics.destroy();
}

/** Um halo baixo, para a caixa não flutuar sobre nada. */
function makeHaloTexture(scene: TextureScene): void {
  if (scene.textures.exists(TEXTURE.halo)) {
    return;
  }
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  const centre = HALO_PX / 2;
  for (let step = 5; step >= 1; step -= 1) {
    graphics.fillStyle(0xffffff, 0.05);
    graphics.fillCircle(centre, centre, (centre * step) / 5);
  }
  graphics.generateTexture(TEXTURE.halo, HALO_PX, HALO_PX);
  graphics.destroy();
}

/** Uma dobra determinística sobre o id. Nada aqui usa um gerador. */
function fold(id: string): number {
  let value = 0x811c_9dc5;
  for (let at = 0; at < id.length; at += 1) {
    value = Math.imul(value ^ id.charCodeAt(at), 0x0100_0193);
  }
  return (value ^ (value >>> 16)) >>> 0;
}

export class OpenWorldBoxArt {
  private readonly views = new Map<string, BoxView>();

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly depth: number,
  ) {
    const target = scene as unknown as TextureScene;
    makeBodyTexture(target);
    makeGlowTexture(target);
    makeHaloTexture(target);
  }

  update(
    boxes: readonly BoxDrawState[],
    nearestId: string | null,
    nowMs: number,
    worldUnits: (mu: number) => number,
    size: number,
  ): void {
    const alive = new Set<string>();

    for (const box of boxes) {
      alive.add(box.id);
      const view = this.viewFor(box.id);
      const x = worldUnits(box.x);
      const y = worldUnits(box.y);
      /*
       * A fase da pulsação sai do id: as caixas de um campo de batalha não
       * respiram todas ao mesmo tempo, e nada disso depende de um sorteio.
       */
      const phase = (fold(box.id) % 1_000) / 160;
      const pulse = Math.sin(nowMs / 1_600 + phase);
      const near = box.id === nearestId;
      /*
       * **Perto do fim ela pisca**, e isso é o único uso do tempo restante no
       * desenho: uma caixa a dois minutos e uma a dez são iguais.
       */
      const fading = box.ttl <= FADING_MS;
      const urgency = fading ? 0.45 + Math.abs(Math.sin(nowMs / 260)) * 0.55 : 1;

      view.body.setPosition(x, y).setDisplaySize(size, size).setTint(BODY_TINT).setAlpha(urgency);
      view.glow
        .setPosition(x, y - size * 0.06)
        .setDisplaySize(size * 0.9, size * 0.9)
        .setTint(near ? REWARD_TINT : CLASP_TINT)
        .setAlpha((near ? 0.85 : 0.5) * urgency + pulse * 0.08);
      view.halo
        .setPosition(x, y + size * 0.18)
        .setDisplaySize(size * (near ? 2.2 : 1.6), size * (near ? 1.1 : 0.8))
        .setTint(near ? REWARD_TINT : HALO_TINT)
        .setAlpha((near ? 0.3 : 0.16) * urgency);
    }

    for (const [id, view] of this.views) {
      if (alive.has(id)) {
        continue;
      }
      view.body.destroy();
      view.glow.destroy();
      view.halo.destroy();
      this.views.delete(id);
    }
  }

  private viewFor(id: string): BoxView {
    const existing = this.views.get(id);
    if (existing !== undefined) {
      return existing;
    }
    const halo = this.scene.add
      .image(0, 0, TEXTURE.halo)
      .setDepth(this.depth - 1)
      .setBlendMode(Phaser.BlendModes.ADD);
    const body = this.scene.add.image(0, 0, TEXTURE.body).setDepth(this.depth);
    const glow = this.scene.add
      .image(0, 0, TEXTURE.glow)
      .setDepth(this.depth + 1)
      .setBlendMode(Phaser.BlendModes.ADD);
    const view: BoxView = { body, glow, halo };
    this.views.set(id, view);
    return view;
  }

  destroy(): void {
    for (const view of this.views.values()) {
      view.body.destroy();
      view.glow.destroy();
      view.halo.destroy();
    }
    this.views.clear();
  }
}

/**
 * **O aro de um aliado.** Uma textura, criada uma vez e partilhada por todos.
 *
 * Um anel fino, ciano, com o interior vazio: ele diz "esta nave voa comigo" sem
 * tapar o casco, a barra de vida, o nickname, as cartas ou os sinais de debuff.
 * Nada de partículas e nada de halo por cima da nave — quem voa com quem é
 * útil, e nenhuma dessas coisas é mais urgente do que saber quem está a morrer.
 *
 * Desenhada com o alfa a decair para fora, para o anel não ter uma borda dura
 * sobre um fundo escuro. Nunca recriada: uma segunda chamada devolve a mesma
 * chave, e a textura vive enquanto o jogo viver.
 */
export function ensureAllyRingTexture(scene: Phaser.Scene): string {
  const key = 'open-world-ally-ring';
  if (scene.textures.exists(key)) {
    return key;
  }
  const size = 128;
  const graphics = (scene as unknown as TextureScene).make.graphics({ x: 0, y: 0 }, false);
  const centre = size / 2;
  for (let step = 0; step < 6; step += 1) {
    graphics.lineStyle(3 - step * 0.4, 0xffffff, 0.5 - step * 0.07);
    graphics.strokeCircle(centre, centre, centre - 6 - step * 2);
  }
  graphics.generateTexture(key, size, size);
  graphics.destroy();
  return key;
}
